import { Typography, useTheme } from "@mui/joy";
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  Tabs,
  Tab,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  IconButton,
  Fade,
} from "@mui/material";
import {
  TopicTree,
  useGetStatsCoursesByCourseIdQuery,
  useGetStatsCoursesByCourseIdTopicsQuery,
  useGetStatsCoursesByCourseIdUsersQuery,
  useGetOrganizationsQuery,
} from "../../../store/api/umbraco-api";
import StatCard from "./stat-card";
import {
  AccessTime,
  Quiz,
  KeyboardArrowDown,
  Clear,
  Info,
  Refresh,
} from "@mui/icons-material";
import { palette } from "../../../theme";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useCourses } from "../../../context/courses-context";

// Add date filter type enum
type DateFilterType =
  | "allTime"
  | "lastDay"
  | "lastWeek"
  | "lastMonth"
  | "custom";

const formatLearningTime = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;
};

// Modify the renderTopicMenuItem function
const renderTopicMenuItem = (
  topic: TopicTree,
  level: number = 0
): React.ReactNode[] => {
  return [
    <MenuItem
      key={topic.id}
      value={topic.id}
      sx={{
        pl: 2 * level + 2,
        "&.Mui-selected": {
          backgroundColor: palette.purple_light,
        },
        "&:hover": {
          backgroundColor: palette.purple_light,
        },
        fontWeight: level === 0 ? 600 : 400,
        color: level === 0 ? palette.purple : "inherit",
      }}
    >
      {topic.name || ""}
    </MenuItem>,
    ...(topic.children || []).flatMap((child) =>
      renderTopicMenuItem(child, level + 1)
    ),
  ];
};

const TeacherStatisticsScreen = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { setSelectedCourse, selectedCourseData } = useCourses();
  const [courses, setCourses] = useState<{ id: number; name: string }[]>([]);
  const [activeTab, setActiveTab] = React.useState(0);
  const [startTime, setStartTime] = React.useState("");
  const [endTime, setEndTime] = React.useState("");
  const [selectedTopicId, setSelectedTopicId] = React.useState<
    number | undefined
  >();
  const [tempStartTime, setTempStartTime] = React.useState("");
  const [tempEndTime, setTempEndTime] = React.useState("");
  const [selectedParentTopic, setSelectedParentTopic] = React.useState<
    TopicTree | undefined
  >();
  const [selectedChildTopic, setSelectedChildTopic] = React.useState<
    TopicTree | undefined
  >();
  const [showFilters, setShowFilters] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [dateFilterType, setDateFilterType] =
    useState<DateFilterType>("allTime");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: organizationsData } = useGetOrganizationsQuery();

  // Load courses from organizations data
  useEffect(() => {
    if (organizationsData) {
      const allCourses = organizationsData.flatMap(
        (org) =>
          org.courses
            ?.map((course) => ({
              id: course.id,
              name: course.name,
            }))
            .filter(
              (course): course is { id: number; name: string } =>
                course.id !== undefined && course.name !== undefined
            ) || []
      );

      setCourses(allCourses);

      // Set the first course as default if no course is selected
      if (allCourses.length > 0 && !selectedCourseData.id) {
        const firstCourse = allCourses[0];

        setSelectedCourse(firstCourse);
      }
    }
  }, [organizationsData, dispatch, selectedCourseData.id]);

  const { data: courseStats, refetch: refetchCourseStats } =
    useGetStatsCoursesByCourseIdQuery({
      courseId: selectedCourseData.id || 0,
      startTime: activeTab === 0 ? startTime : undefined,
      endTime: activeTab === 0 ? endTime : undefined,
      topicId: selectedTopicId,
    });

  const { data: topics } = useGetStatsCoursesByCourseIdTopicsQuery({
    courseId: selectedCourseData.id || 0,
  });

  const { data: userStats, refetch: refetchUserStats } =
    useGetStatsCoursesByCourseIdUsersQuery({
      courseId: selectedCourseData.id || 0,
      startTime: activeTab === 0 ? startTime : undefined,
      endTime: activeTab === 0 ? endTime : undefined,
      topicId: selectedTopicId,
    });

  // Sort userStats by totalAnswerCount in descending order
  const sortedUserStats = React.useMemo(() => {
    if (!userStats) return [];
    let sortedData = [...userStats];

    // Apply search filter
    if (searchQuery) {
      sortedData = sortedData.filter((user) =>
        user.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (sortConfig) {
      sortedData.sort((a, b) => {
        if (sortConfig.key === "userName") {
          // Use localeCompare for proper string comparison of Hebrew and English
          const aName = a.name || "";
          const bName = b.name || "";
          return sortConfig.direction === "asc"
            ? aName.localeCompare(bName, ["he", "en"])
            : bName.localeCompare(aName, ["he", "en"]);
        }

        const aValue = a.stats?.[sortConfig.key as keyof typeof a.stats] || 0;
        const bValue = b.stats?.[sortConfig.key as keyof typeof b.stats] || 0;

        if (sortConfig.direction === "asc") {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    } else {
      // Default sort by totalAnswerCount if no sort config
      sortedData.sort((a, b) => {
        const aCount = a.stats?.totalAnswerCount || 0;
        const bCount = b.stats?.totalAnswerCount || 0;
        return bCount - aCount;
      });
    }

    return sortedData;
  }, [userStats, sortConfig, searchQuery]);

  const handleSort = (key: string) => {
    setSortConfig((current) => {
      if (current?.key === key) {
        // If clicking the same column, toggle direction
        return {
          key,
          direction: current.direction === "asc" ? "desc" : "asc",
        };
      }
      // If clicking a new column, sort descending by default
      return {
        key,
        direction: "desc",
      };
    });
  };

  // Add effect to automatically apply filter when date filter type changes
  useEffect(() => {
    if (dateFilterType !== "custom") {
      // Only auto-apply for predefined ranges, not for custom
      handleApplyDates();
    }
  }, [dateFilterType]);

  // Add effect to apply filter when component mounts
  useEffect(() => {
    // Apply filter on initial load
    if (dateFilterType === "allTime") {
      handleApplyDates();
    }
  }, []);

  // Learning minutes debugging
  useEffect(() => {
    if (courseStats) {
      console.group("📊 Course Stats Learning Minutes - Teacher");
      console.log("🎯 Course Stats:", {
        courseId: selectedCourseData.id,
        totalAnswerCount: courseStats?.totalAnswerCount,
        correctAnswerCount: courseStats?.correctAnswerCount,
        learningMinutes: courseStats?.learningMinutes,
        learningHours: courseStats?.learningMinutes ? (courseStats.learningMinutes / 60).toFixed(2) : 0,
        topicId: selectedTopicId,
        startTime: startTime,
        endTime: endTime
      });
      
      if ((courseStats?.learningMinutes || 0) === 0) {
        console.warn("⚠️ Course learning minutes is 0 - check:");
        console.log("- Unit completion tracking");
        console.log("- Activity API calls");
        console.log("- Backend learning minutes calculation");
        console.log("- Date filter settings");
      }
      
      console.groupEnd();
    }
  }, [courseStats, selectedCourseData.id, selectedTopicId, startTime, endTime]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleApplyDates = () => {
    setIsLoading(true);

    // Handle different date filter types
    if (dateFilterType === "custom") {
      setStartTime(tempStartTime);
      setEndTime(tempEndTime);
    } else if (dateFilterType === "allTime") {
      // For 'allTime', set empty strings to indicate no date filtering
      setStartTime("");
      setEndTime("");
    } else {
      const now = new Date();
      let startDate = new Date();

      switch (dateFilterType) {
        case "lastDay":
          startDate.setDate(now.getDate() - 1);
          break;
        case "lastWeek":
          startDate.setDate(now.getDate() - 7);
          break;
        case "lastMonth":
          startDate.setMonth(now.getMonth() - 1);
          break;
      }

      // Format dates as YYYY-MM-DD
      const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const formattedStartDate = formatDate(startDate);
      const formattedEndDate = formatDate(now);

      console.log(
        "Setting date range:",
        formattedStartDate,
        "to",
        formattedEndDate
      );

      setStartTime(formattedStartDate);
      setEndTime(formattedEndDate);
    }

    // Simulate loading state
    setTimeout(() => {
      setIsLoading(false);
      // Trigger data refresh after setting the new date range
      refetchCourseStats();
      refetchUserStats();
    }, 800);
  };

  const handleRefresh = () => {
    setIsLoading(true);
    refetchCourseStats();
    refetchUserStats();

    // Simulate loading state
    setTimeout(() => {
      setIsLoading(false);
    }, 800);
  };

  // Update the title in the Topics tab content to show the full path
  const getTopicPath = () => {
    if (selectedChildTopic) {
      return `${selectedParentTopic?.name || ""} > ${selectedChildTopic.name}`;
    }
    return selectedParentTopic?.name || "";
  };

  // Handle course selection
  const handleCourseChange = (courseId: number) => {
    const selectedCourse = courses.find((course) => course.id === courseId);
    if (selectedCourse) {
      setSelectedCourse(selectedCourse);
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto",
        p: { xs: 2, md: 4 },
        direction: "rtl",
        bgcolor: "#f9f9f9",
      }}
    >
      {/* Title on top */}
      <Typography
        level="h2"
        sx={{
          fontSize: { xs: "1.2rem", md: "1.5rem" },
          fontWeight: "bold",
          color: palette.purple,
          mb: 2,
        }}
      >
        סטטיסטיקות קורס
      </Typography>

      {/* Combined Controls Bar */}
      <Box
        sx={{
          bgcolor: "white",
          p: 2,
          borderRadius: 2,
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          gap: 2,
        }}
      >
        {/* Left side: Course Selector */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
          <FormControl sx={{ minWidth: 200, maxWidth: 300 }}>
            <Select
              value={selectedCourseData.id || ""}
              onChange={(event) =>
                handleCourseChange(event.target.value as number)
              }
              size="small"
              sx={{
                backgroundColor: "white",
                "& .MuiSelect-select": {
                  py: 1,
                  fontWeight: "bold",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: palette.purple_light,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: palette.purple,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: palette.purple,
                },
              }}
              IconComponent={(props) => (
                <KeyboardArrowDown {...props} sx={{ color: palette.purple }} />
              )}
            >
              {courses.map((course) => (
                <MenuItem
                  key={course.id}
                  value={course.id}
                  sx={{
                    "&.Mui-selected": {
                      backgroundColor: palette.purple_light,
                    },
                    "&:hover": {
                      backgroundColor: palette.purple_light,
                    },
                  }}
                >
                  {course.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Middle: Tabs */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            justifyContent: "center",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                color: palette.gray,
                fontWeight: 600,
                py: 1,
                minWidth: "auto",
                px: 2,
                "&.Mui-selected": {
                  color: palette.purple,
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: palette.purple,
                height: 3,
              },
            }}
          >
            <Tab label="פעילות" />
            <Tab label="לפי נושאים" />
          </Tabs>
        </Box>

        {/* Right side: All Filters and Refresh */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          {/* Date Filter Type Dropdown - Only show in Activity tab */}
          {activeTab === 0 && (
            <FormControl size="small" sx={{ minWidth: 150, maxWidth: 200 }}>
              <Select
                value={dateFilterType}
                onChange={(event) =>
                  setDateFilterType(event.target.value as DateFilterType)
                }
                displayEmpty
                sx={{
                  backgroundColor: "white",
                  "& .MuiSelect-select": {
                    py: 1,
                    fontWeight: "bold",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: palette.purple_light,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: palette.purple,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: palette.purple,
                  },
                }}
              >
                <MenuItem value="allTime">כל הזמן</MenuItem>
                <MenuItem value="lastDay">היום האחרון</MenuItem>
                <MenuItem value="lastWeek">השבוע האחרון</MenuItem>
                <MenuItem value="lastMonth">החודש האחרון</MenuItem>
                <MenuItem value="custom">תאריכים מותאמים אישית</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Topic Filter - Only show in Topics tab */}
          {activeTab === 1 && (
            <FormControl size="small" sx={{ minWidth: 150, maxWidth: 200 }}>
              <Select
                value={selectedParentTopic?.id ?? ""}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === "") {
                    // When "All Topics" is selected
                    setSelectedParentTopic(undefined);
                    setSelectedChildTopic(undefined);
                    setSelectedTopicId(undefined);
                  } else {
                    const selected = topics?.find((t) => t.id === value);
                    setSelectedParentTopic(selected);
                    setSelectedChildTopic(undefined); // Reset child selection
                    setSelectedTopicId(Number(value)); // Set parent ID as selected topic
                  }
                }}
                displayEmpty
                sx={{
                  backgroundColor: "white",
                  "& .MuiSelect-select": {
                    py: 1,
                    fontWeight: "bold",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: palette.purple_light,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: palette.purple,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: palette.purple,
                  },
                }}
              >
                <MenuItem value="">
                  <em>כל הנושאים</em>
                </MenuItem>
                {topics?.map((topic) => (
                  <MenuItem
                    key={topic.id}
                    value={topic.id}
                    sx={{
                      fontWeight: 600,
                      color: palette.purple,
                      "&.Mui-selected": {
                        backgroundColor: palette.purple_light,
                      },
                      "&:hover": {
                        backgroundColor: palette.purple_light,
                      },
                    }}
                  >
                    {topic.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Tooltip title="רענן נתונים">
            <IconButton
              onClick={handleRefresh}
              size="small"
              sx={{
                color: palette.purple,
                bgcolor: `${palette.purple}10`,
                "&:hover": { bgcolor: `${palette.purple}20` },
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Custom Date Range - Only show when custom date filter is selected */}
      {activeTab === 0 && dateFilterType === "custom" && (
        <Box
          sx={{
            bgcolor: "white",
            p: 2,
            borderRadius: 2,
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
            mb: 3,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { xs: "stretch", sm: "center" },
          }}
        >
          <TextField
            type="date"
            label="מתאריך"
            value={tempStartTime}
            onChange={(event) => setTempStartTime(event.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: palette.purple_light },
                "&:hover fieldset": { borderColor: palette.purple },
                "&.Mui-focused fieldset": { borderColor: palette.purple },
              },
            }}
          />
          <TextField
            type="date"
            label="עד תאריך"
            value={tempEndTime}
            onChange={(event) => setTempEndTime(event.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: palette.purple_light },
                "&:hover fieldset": { borderColor: palette.purple },
                "&.Mui-focused fieldset": { borderColor: palette.purple },
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleApplyDates}
            disabled={isLoading}
            startIcon={
              isLoading ? (
                <Refresh sx={{ animation: "spin 1s linear infinite" }} />
              ) : null
            }
            size="small"
            sx={{
              bgcolor: palette.purple,
              "&:hover": {
                bgcolor: palette.purple_dark,
              },
              minWidth: "100px",
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          >
            {isLoading ? "מעדכן..." : "החל"}
          </Button>
        </Box>
      )}

      {/* Child Topic Dropdown - Only show if parent has children */}
      {activeTab === 1 &&
        selectedParentTopic?.children &&
        selectedParentTopic.children.length > 0 && (
          <Box
            sx={{
              bgcolor: "white",
              p: 2,
              borderRadius: 2,
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              level="body-sm"
              sx={{
                fontWeight: 600,
                color: palette.gray,
                whiteSpace: "nowrap",
              }}
            >
              תת-נושא:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200, maxWidth: 300 }}>
              <Select
                value={selectedChildTopic?.id ?? ""}
                onChange={(event) => {
                  const value = event.target.value;
                  if (selectedParentTopic) {
                    if (value === "") {
                      // When "All Subtopics" is selected
                      setSelectedChildTopic(undefined);
                      setSelectedTopicId(selectedParentTopic.id);
                    } else {
                      const selected = selectedParentTopic.children?.find(
                        (t) => t.id === value
                      );
                      setSelectedChildTopic(selected);
                      setSelectedTopicId(Number(value));
                    }
                  }
                }}
                displayEmpty
                sx={{
                  backgroundColor: "white",
                  "& .MuiSelect-select": {
                    py: 1,
                    fontWeight: "bold",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: palette.purple_light,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: palette.purple,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: palette.purple,
                  },
                }}
              >
                <MenuItem value="">
                  <em>כל תתי-הנושאים</em>
                </MenuItem>
                {selectedParentTopic.children?.map((topic) => (
                  <MenuItem
                    key={topic.id}
                    value={topic.id}
                    sx={{
                      "&.Mui-selected": {
                        backgroundColor: palette.purple_light,
                      },
                      "&:hover": {
                        backgroundColor: palette.purple_light,
                      },
                    }}
                  >
                    {topic.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Clear Button - Only show if there's a selection */}
            {selectedParentTopic && (
              <Button
                variant="outlined"
                onClick={() => {
                  setSelectedParentTopic(undefined);
                  setSelectedChildTopic(undefined);
                  setSelectedTopicId(undefined);
                }}
                startIcon={<Clear />}
                size="small"
                sx={{
                  borderColor: palette.purple,
                  color: palette.purple,
                  "&:hover": {
                    borderColor: palette.purple_dark,
                    backgroundColor: palette.purple_light,
                  },
                  borderRadius: 2,
                }}
              >
                נקה בחירה
              </Button>
            )}
          </Box>
        )}

      {/* Content Area */}
      {activeTab === 0 ? (
        // Activity Tab Content
        <>
          {/* Stats Cards */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
              },
              gap: 3,
              mb: 4,
            }}
          >
            <StatCard
              icon={<Quiz sx={{ fontSize: 40, color: palette.purple }} />}
              title="סה״כ יחידות שנלמדו במערכת"
              value={courseStats?.totalAnswerCount || 0}
              isTimeValue={false}
            />
            <StatCard
              icon={<AccessTime sx={{ fontSize: 40, color: palette.blue }} />}
              title="סה״כ זמן שנלמד במערכת"
              value={courseStats?.learningMinutes || 0}
              isTimeValue={true}
            />
          </Box>

          {/* User Stats Table */}
          <Box
            sx={{
              mt: 4,
              bgcolor: "white",
              borderRadius: 2,
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 3,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                level="h3"
                sx={{
                  fontSize: { xs: "1.2rem", md: "1.5rem" },
                  fontWeight: "bold",
                  color: palette.purple,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Info fontSize="small" />
                סטטיסטיקות משתמשים
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <TextField
                  size="small"
                  placeholder="חיפוש לפי שם..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{
                    minWidth: 200,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "& fieldset": {
                        borderColor: palette.purple_light,
                      },
                      "&:hover fieldset": {
                        borderColor: palette.purple,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: palette.purple,
                      },
                    },
                  }}
                />
                <Chip
                  label={`${sortedUserStats.length} משתמשים`}
                  sx={{
                    bgcolor: `${palette.purple}15`,
                    color: palette.purple,
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>

            <TableContainer
              sx={{
                "& .MuiTableCell-root": {
                  textAlign: "right",
                  padding: "16px",
                },
                maxHeight: "500px",
                overflow: "auto",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: `${palette.purple}10`,
                      "& th": {
                        fontWeight: 700,
                        color: palette.purple,
                      },
                    }}
                  >
                    <TableCell
                      onClick={() => handleSort("userName")}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                      }}
                    >
                      User Name
                      {sortConfig?.key === "userName" && (
                        <KeyboardArrowDown
                          sx={{
                            transform:
                              sortConfig.direction === "asc"
                                ? "rotate(180deg)"
                                : "none",
                            transition: "transform 0.2s",
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort("totalAnswerCount")}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                      }}
                    >
                      Total Answers
                      {sortConfig?.key === "totalAnswerCount" && (
                        <KeyboardArrowDown
                          sx={{
                            transform:
                              sortConfig.direction === "asc"
                                ? "rotate(180deg)"
                                : "none",
                            transition: "transform 0.2s",
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort("learningTime")}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                      }}
                    >
                      Learning Time
                      {sortConfig?.key === "learningTime" && (
                        <KeyboardArrowDown
                          sx={{
                            transform:
                              sortConfig.direction === "asc"
                                ? "rotate(180deg)"
                                : "none",
                            transition: "transform 0.2s",
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedUserStats.map((user) => (
                    <TableRow
                      key={user.id}
                      sx={{
                        "&:hover": { backgroundColor: "#f5f5f5" },
                        "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>
                        {user.name}
                      </TableCell>
                      <TableCell>{user.stats?.totalAnswerCount || 0}</TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                          onClick={() => {
                            // User Stats Learning Minutes Debug
                            console.group("📊 User Stats Learning Minutes");
                            console.log("🎯 User Stats:", {
                              userId: user.id,
                              userName: user.name,
                              totalAnswerCount: user.stats?.totalAnswerCount,
                              correctAnswerCount: user.stats?.correctAnswerCount,
                              learningMinutes: user.stats?.learningMinutes,
                              learningHours: user.stats?.learningMinutes ? (user.stats.learningMinutes / 60).toFixed(2) : 0
                            });
                            
                            if ((user.stats?.learningMinutes || 0) === 0) {
                              console.warn("⚠️ User learning minutes is 0 for", user.name);
                              console.log("- Check if user has completed any units");
                              console.log("- Verify activity tracking for this user");
                            }
                            
                            console.groupEnd();
                          }}
                        >
                          <Typography
                            component="span"
                            sx={{
                              color: (user.stats?.learningMinutes || 0) === 0
                                ? palette.gray_dark
                                : palette.purple,
                              fontWeight: (user.stats?.learningMinutes || 0) > 0 ? 600 : "normal",
                            }}
                          >
                            {formatLearningTime(user.stats?.learningMinutes || 0)}
                          </Typography>
                          <AccessTime fontSize="small" sx={{ color: palette.gray }} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      ) : (
        // Topics Tab Content
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
              },
              gap: 3,
              mb: 4,
            }}
          >
            <StatCard
              icon={<Quiz sx={{ fontSize: 40, color: palette.purple }} />}
              title={selectedTopicId ? "שאלות בנושא" : "סך הכל שאלות"}
              value={courseStats?.totalAnswerCount || 0}
              isTimeValue={false}
            />
            <StatCard
              icon={<AccessTime sx={{ fontSize: 40, color: palette.blue }} />}
              title={selectedTopicId ? "זמן למידה בנושא" : "סך הכל זמן למידה"}
              value={courseStats?.learningMinutes || 0}
              isTimeValue={true}
            />
          </Box>

          {/* User Stats Table */}
          <Box
            sx={{
              mt: 4,
              bgcolor: "white",
              borderRadius: 2,
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 3,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                level="h3"
                sx={{
                  fontSize: { xs: "1.2rem", md: "1.5rem" },
                  fontWeight: "bold",
                  color: palette.purple,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Info fontSize="small" />
                {selectedTopicId
                  ? `סטטיסטיקות משתמשים בנושא ${getTopicPath()}`
                  : "סטטיסטיקות משתמשים בכל הנושאים"}
              </Typography>

              <Chip
                label={`${userStats?.length || 0} משתמשים`}
                sx={{
                  bgcolor: `${palette.purple}15`,
                  color: palette.purple,
                  fontWeight: 600,
                }}
              />
            </Box>

            <TableContainer
              sx={{
                "& .MuiTableCell-root": {
                  textAlign: "right",
                  padding: "16px",
                },
                maxHeight: "500px",
                overflow: "auto",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: `${palette.purple}10`,
                      "& th": {
                        fontWeight: 700,
                        color: palette.purple,
                      },
                    }}
                  >
                    <TableCell
                      onClick={() => handleSort("userName")}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                      }}
                    >
                      User Name
                      {sortConfig?.key === "userName" && (
                        <KeyboardArrowDown
                          sx={{
                            transform:
                              sortConfig.direction === "asc"
                                ? "rotate(180deg)"
                                : "none",
                            transition: "transform 0.2s",
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort("totalAnswerCount")}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                      }}
                    >
                      Total Answers
                      {sortConfig?.key === "totalAnswerCount" && (
                        <KeyboardArrowDown
                          sx={{
                            transform:
                              sortConfig.direction === "asc"
                                ? "rotate(180deg)"
                                : "none",
                            transition: "transform 0.2s",
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort("correctAnswerCount")}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                      }}
                    >
                      Correct Answers
                      {sortConfig?.key === "correctAnswerCount" && (
                        <KeyboardArrowDown
                          sx={{
                            transform:
                              sortConfig.direction === "asc"
                                ? "rotate(180deg)"
                                : "none",
                            transition: "transform 0.2s",
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort("learningTime")}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                      }}
                    >
                      Learning Time
                      {sortConfig?.key === "learningTime" && (
                        <KeyboardArrowDown
                          sx={{
                            transform:
                              sortConfig.direction === "asc"
                                ? "rotate(180deg)"
                                : "none",
                            transition: "transform 0.2s",
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userStats?.map((user) => {
                    return (
                      <TableRow
                        key={user.id}
                        sx={{
                          "&:hover": { backgroundColor: "#f5f5f5" },
                          "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>
                          {user.name}
                        </TableCell>
                        <TableCell>
                          {user.stats?.totalAnswerCount || 0}
                        </TableCell>
                        <TableCell>
                          {user.stats?.correctAnswerCount || 0}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              component="span"
                              sx={{
                                color: (user.stats?.learningMinutes || 0) === 0
                                  ? palette.gray_dark
                                  : palette.purple,
                                fontWeight: (user.stats?.learningMinutes || 0) > 0 ? 600 : "normal",
                              }}
                            >
                              {formatLearningTime(user.stats?.learningMinutes || 0)}
                            </Typography>
                            <AccessTime fontSize="small" sx={{ color: palette.gray }} />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}
    </Box>
  );
};

export default TeacherStatisticsScreen;
